import { useCallback, useRef, useState } from 'react';

import {
  anchorReferenceFromElement,
  HOVER_POPOVER_CLOSE_MS,
  HOVER_POPOVER_OPEN_MS,
  type HoverPopoverAnchorReference,
  type HoverPopoverTiming,
} from './HoverPopover.types';
import { useHoverIntent } from './useHoverIntent';

/**
 * One floating popover shared across many hover triggers, keyed by an id —
 * the calendar pattern: hovering an event opens its card after a rest, sliding
 * to a neighbouring event swaps the anchor instantly, leaving closes after a
 * grace period, and a press inside the card pins it open until dismissed.
 *
 * The hook owns *intent* (which key is open, the active anchor, pinning, the
 * timers). Render the result with <HoverPopoverSurface> and your own content.
 */
export const useKeyedHoverPopover = <TKey extends string = string>(
  timing?: HoverPopoverTiming,
) => {
  const openDelayMs = timing?.openDelayMs ?? HOVER_POPOVER_OPEN_MS;
  const closeDelayMs = timing?.closeDelayMs ?? HOVER_POPOVER_CLOSE_MS;

  const [openKey, setOpenKey] = useState<TKey | null>(null);
  const [anchorReference, setAnchorReference] =
    useState<HoverPopoverAnchorReference | null>(null);
  const isPinnedRef = useRef(false);

  const { scheduleOpen, scheduleClose, cancelOpen, cancelClose, clearAll } =
    useHoverIntent();

  const close = useCallback(() => {
    clearAll();
    isPinnedRef.current = false;
    setOpenKey(null);
    setAnchorReference(null);
  }, [clearAll]);

  const pin = useCallback(() => {
    clearAll();
    isPinnedRef.current = true;
  }, [clearAll]);

  const onTriggerEnter = useCallback(
    (key: TKey, anchorEl: HTMLElement) => {
      const reference = anchorReferenceFromElement(anchorEl);
      cancelClose();

      // Same trigger — just keep the anchor fresh (re-layout/scroll).
      if (openKey === key) {
        setAnchorReference(reference);
        return;
      }

      // A different card is already open (and not pinned) — swap instantly,
      // no second rest delay.
      if (openKey && !isPinnedRef.current) {
        cancelOpen();
        setOpenKey(key);
        setAnchorReference(reference);
        return;
      }

      // Cold open — wait out the rest delay before showing.
      scheduleOpen(() => {
        isPinnedRef.current = false;
        setOpenKey(key);
        setAnchorReference(reference);
      }, openDelayMs);
    },
    [openKey, cancelClose, cancelOpen, scheduleOpen, openDelayMs],
  );

  const onTriggerLeave = useCallback(
    (key?: TKey) => {
      if (isPinnedRef.current) return;
      // Ignore stale leaves from a card that isn't the one currently open.
      if (key && openKey && key !== openKey) return;
      cancelOpen();
      scheduleClose(close, closeDelayMs);
    },
    [openKey, cancelOpen, scheduleClose, close, closeDelayMs],
  );

  return {
    openKey,
    anchorReference,
    /** Trigger handlers — wire to the hover target's onMouseEnter/Leave. */
    onTriggerEnter,
    onTriggerLeave,
    /** Surface handlers — wire to <HoverPopoverSurface>. */
    onSurfacePointerEnter: cancelClose,
    onSurfacePointerLeave: () => onTriggerLeave(openKey ?? undefined),
    onInteractionPin: pin,
    close,
  };
};
